import { useMemo, useState, memo } from "react";
import { getThumbnailUrl, getMediumImageUrl, getFullImageUrl } from "@/utils/imageUtils";

type ImageSize = "thumbnail" | "medium" | "full";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  size?: ImageSize;
  sizes?: string;
}

const getOptimizedSrc = (src: string, size: ImageSize): string => {
  switch (size) {
    case "thumbnail":
      return getThumbnailUrl(src);
    case "medium":
      return getMediumImageUrl(src);
    case "full":
      return getFullImageUrl(src);
    default:
      return getThumbnailUrl(src);
  }
};

const OptimizedImage = memo(
  ({ src, alt, className, priority = false, size = "thumbnail", sizes }: OptimizedImageProps) => {
    const optimizedSrc = useMemo(() => getOptimizedSrc(src, size), [src, size]);
    const [loadedUrl, setLoadedUrl] = useState("");
    const [failedOptimizedUrl, setFailedOptimizedUrl] = useState("");
    const displayUrl = failedOptimizedUrl === optimizedSrc ? src : optimizedSrc;
    const isLoaded = loadedUrl === displayUrl;

    return (
      <img
        key={displayUrl}
        src={displayUrl}
        alt={alt}
        width={size === "full" ? 1600 : size === "medium" ? 900 : 480}
        height={size === "full" ? 1600 : size === "medium" ? 900 : 480}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "low"}
        decoding="async"
        onLoad={() => setLoadedUrl(displayUrl)}
        onError={() => {
          if (displayUrl === optimizedSrc && optimizedSrc !== src) {
            setFailedOptimizedUrl(optimizedSrc);
          }
        }}
        className={`${className ?? ""} ${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
        style={{ contentVisibility: "auto" }}
      />
    );
  }
);

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
